/**
 * The session in progress as the navigation shows it (A2 `nav.tsx:36-51`):
 * the elapsed clock, or the rest countdown while resting, and how many of
 * today's sets are logged.
 *
 * The data is T53's `useLiveSessionBadge()`, which re-renders only when the
 * slice it selects moves. The clock is the shell's own, and it ticks ONLY
 * while a session is in `log` or `rest`: A2 ticked whenever the shell was
 * mounted, which re-rendered the whole frame at 1 Hz forever.
 */
import { formatCountdown } from "@repo/client/session";
import { useEffect, useState } from "react";
import { clockMs, isPaused, useLiveSessionBadge } from "../workout/store";

export interface LiveSessionView {
  /** `m:ss` — the rest left while resting, else the time since the start. */
  readonly clock: string;
  readonly done: number;
  /** The clock is paused (`D245`): it stands still, and says so. */
  readonly paused: boolean;
  readonly resting: boolean;
  /** At least 1, so the ring's fraction is always defined. */
  readonly total: number;
}

const TICK_MS = 1000;

/** `Date.now()`, refreshed every second while `running`, frozen otherwise. */
function useNow(running: boolean): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!running) {
      return;
    }
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [running]);
  return now;
}

/** Elapsed time as `m:ss`, rounded down (`formatCountdown` rounds up). */
function elapsedClock(ms: number): string {
  return formatCountdown(Math.floor(Math.max(0, ms) / TICK_MS) * TICK_MS);
}

/** Null before the session starts (readiness), once it ends, and with none. */
export function useLiveSession(): LiveSessionView | null {
  const badge = useLiveSessionBadge();
  const running =
    badge !== null && (badge.phase === "log" || badge.phase === "rest");
  const paused = badge !== null && isPaused(badge);
  const now = useNow(running && !paused);
  if (!(running && badge)) {
    return null;
  }
  const restLeft =
    badge.phase === "rest" && badge.restEndsAt !== null
      ? badge.restEndsAt - now
      : 0;
  const resting = restLeft > 0;
  return {
    clock: resting
      ? formatCountdown(restLeft)
      : elapsedClock(
          clockMs(
            { endedAt: null, pauses: badge.pauses, startedAt: badge.startedAt },
            now
          )
        ),
    done: badge.doneSets,
    paused,
    resting,
    total: Math.max(1, badge.totalSets),
  };
}
